import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import withRouter from '../../hoc/withRouter';
import withConfig from '../../hoc/withConfig';
import { debounce } from '../../utils/debounceUtils';
import {
  PubSubEvents,
  BookingFormOptions,
  GaAnalyticEvents,
  Routes,
} from '../../constants';
import Events from '../../events';
import {
  parseQueryStrings,
  stringifyQueryStrings,
} from '../../utils/urlParamUtils';
import { trackEvent } from '../../utils/analyticUtils';
import {
  getBaiduUserAddressByCoords,
  getUserAddressByCoords,
} from '../../utils/geocodeUtils';
import { pluckBaiduCityState } from '../../utils/searchUtils';
import { generateRoute } from '../../utils/routeUtils';

class BaiduMap extends Component {
  /**
   * Initializes a new instance of the Map component.
   * @param {Object} props The component properties.
   */

  constructor(props) {
    super(props);
    this.state = {
      markers: [],
      showUserLocationOnMap: false,
    };
    this._map = null;
    this._mapRef = createRef();
    this._onSafeMapDrag = debounce(() => this._onMapDragEnd(), 250);
    // this._currMapBounds = null;
    // this._mapInfoWindow = null;
    // this._mapInfoContentCont = null;
    // this._mapSearchAreaButton = null;
    // this._onSafeMapDrag = debounce(() => this._onMapDragEnd(), 250);
    // this._onSafeWindowResize = debounce(() => this._onWindowResize(), 250);
    // this.use_mobile_view = this.props.config.use_mobile_view;
  }

  componentDidMount() {
    const { isDetailsPage } = this.props;

    if (!window.BMap) {
      throw new Error('Baidu Maps must be initialize first.');
    }

    Events.on(PubSubEvents.practiceSelected, this._onSelectMarker);
    if (isDetailsPage) {
      this._initBaiduMapOnDetailsPage();
    } else {
      this._initBaiduMap();
    }
  }

  componentWillUnmount() {
    this._clearPracticeMarkers();
    this._map.removeEventListener('dragging', this._onSafeMapDrag);
    Events.removeListener(PubSubEvents.practiceSelected, this._onSelectMarker);
  }

  componentDidUpdate(prevProps) {
    const { showUserLocationOnMap, markers } = this.state;
    const { practices } = this.props;

    const practiceArrayCompare = (practices1, practices2) => {
      let isEqual = true;

      for (let index = 0; index < practices1?.length; ++index) {
        const practice = practices1[index];

        if (!practices2?.some((p) => practice.practiceId === p.practiceId)) {
          isEqual = false;
          break;
        }
      }

      return isEqual;
    };

    const isSameAddress = () => {
      const { search, hash } = this.props.location;
      const queryStringValues = parseQueryStrings(search, hash);

      const { search: prevSearch, hash: prevHash } = prevProps.location;
      const prevQueryStringValues = parseQueryStrings(prevSearch, prevHash);

      return queryStringValues.address === prevQueryStringValues.address;
    };

    const prevPracticesNotSameWithCurrent = !Array.isEqual(
      prevProps.practices,
      practices,
      practiceArrayCompare
    );

    const currPracticesNotSameWithPrevious = !Array.isEqual(
      practices,
      prevProps.practices,
      practiceArrayCompare
    );

    const shouldRecreateMarkers =
      !isSameAddress() &&
      !showUserLocationOnMap &&
      !prevPracticesNotSameWithCurrent &&
      !currPracticesNotSameWithPrevious &&
      markers.length > practices.length;

    // Recreate the markers everytime the practices change (new search).
    if (
      shouldRecreateMarkers ||
      prevPracticesNotSameWithCurrent ||
      currPracticesNotSameWithPrevious
    ) {
      const { search, hash } = this.props.location;
      const queryStringValues = parseQueryStrings(search, hash);

      this._currAddress = queryStringValues.address;
      this._clearPracticeMarkers();
      this._createPracticeMarkers();
    }
  }

  _onSearchAreaClick = async () => {
    if (this.state.showUserLocationOnMap) {
      this.setState(() => ({ showUserLocationOnMap: false }));
    }

    try {
      const { use_baidu_map } = this.props.config;
      trackEvent(GaAnalyticEvents.map.searchThisAreaClick(this._currAddress));

      this._mapSearchAreaButton.classList.remove(
        this.use_mobile_view
          ? 'mobile__map__search-area--visible'
          : 'map__search-area--visible'
      );

      const mapCenter = this._map.getCenter();

      const lat = parseFloat(mapCenter.lat);
      const lng = parseFloat(mapCenter.lng);
      let shortenedAddress = '';

      const coords = {
        latitude: lat,
        longitude: lng,
      };

      if (use_baidu_map) {
        const { addressComponent } = await getBaiduUserAddressByCoords(coords);
        shortenedAddress = pluckBaiduCityState(addressComponent);
      } else {
        const { components } = await getUserAddressByCoords(coords);
        shortenedAddress = pluckCityState(components);
      }

      const { isFirstLoad, config, navigate, location } = this.props;
      const { search, hash } = location;
      const queryStringValues = parseQueryStrings(search, hash);

      queryStringValues.address = shortenedAddress;
      queryStringValues.lat = lat;
      queryStringValues.long = lng;
      queryStringValues.currentPage = 1;
      queryStringValues.isFirstLoad = isFirstLoad;

      const queryStrings = stringifyQueryStrings(queryStringValues);
      const resultsRoute = generateRoute(config, Routes.results);
      const url = window.location.hash.split('#')[1]?.startsWith('/result')
        ? `?${queryStrings}`
        : `./${resultsRoute}?${queryStrings}`;
      navigate(url);
    } catch (error) {
      console.error(error);
    }
  };

  _createSearchAreaControl = () => {
    if (this._map) {
      const { t } = this.props;
      if (!this._mapSearchAreaButton) {
        const customButton = document.createElement('button');
        customButton.id = 'map-search-area-btn';
        customButton.textContent = t('text_search_this_area');
        customButton.classList.add('map__search-area');
        customButton.addEventListener('click', this._onSearchAreaClick);

        function SearchControl() {
          this.defaultOffset = new window.BMap.Size(20, 20);
          this.searchThisAreaButton = customButton;
        }

        SearchControl.prototype = new window.BMap.Control();
        SearchControl.prototype.initialize = function (map) {
          map.getContainer().appendChild(this.searchThisAreaButton);
          return this.searchThisAreaButton;
        };

        const searchControl = new SearchControl();
        this._mapSearchAreaButton = searchControl.searchThisAreaButton;
        this._map.addControl(searchControl);
      }
    }
  };

  _onMapDragEnd = () => {
    if (
      this._mapSearchAreaButton &&
      !this._mapSearchAreaButton.classList.contains(
        this.use_mobile_view
          ? 'mobile__map__search-area--visible'
          : 'map__search-area--visible'
      )
    ) {
      trackEvent(GaAnalyticEvents.map.scrollMapChange(this._currAddress));

      this._mapSearchAreaButton.classList.add(
        this.use_mobile_view
          ? 'mobile__map__search-area--visible'
          : 'map__search-area--visible'
      );
    }
  };

  _initBaiduMap = () => {
    const { searchLocationCoords } = this.props;

    const { lat, lng } = searchLocationCoords;

    this._map = new window.BMap.Map('baidu-map');
    const initialPoint = new window.BMap.Point(lng, lat);
    this._map.centerAndZoom(initialPoint, 8);
    this._map.highResolutionEnabled();
    this._map.enableScrollWheelZoom(true);
    this._map.addEventListener('dragging', this._onSafeMapDrag);

    this._createPracticeMarkers();
    this._createSearchAreaControl();
  };

  _initBaiduMapOnDetailsPage = () => {
    const { practice } = this.props;
    const { position } = practice;
    const { lat, lng } = position;
    this._map = new window.BMap.Map('baidu-map');
    const initialPoint = new window.BMap.Point(lng, lat);
    this._map.addOverlay(new window.BMap.Marker(initialPoint));
    this._map.centerAndZoom(initialPoint, 16);
    this._map.highResolutionEnabled(true);

    // Add navigation control
    const ctrlOpts = {
      type: window.BMAP_NAVIGATION_CONTROL_ZOOM,
      anchor: window.BMAP_ANCHOR_BOTTOM_RIGHT,
    };
    this._map.addControl(new window.BMap.NavigationControl(ctrlOpts));
  };

  _createPracticeMarkers = () => {
    try {
      const { practices } = this.props;
      const markers = [];
      const points = [];

      if (this._map && practices.length > 0) {
        for (let index = 0; index < practices.length; ++index) {
          const practice = practices[index];
          const { practiceId } = practice;
          const { lat, lng } = practice.position;
          const markerPoint = new window.BMap.Point(lng, lat);
          const marker = new window.BMap.Marker(markerPoint);

          points.push(markerPoint);
          markers.push(marker);

          const markerCustomProp = new window.BMap.Marker({
            map: this._map,
            position: {
              lat: lat,
              lng: lng,
            },
            practiceId: practiceId,
          });

          this._map.addOverlay(marker); // adds each marker to the map
          marker.addEventListener('click', () =>
            this._showPracticeOnSelectMarker(markerCustomProp)
          );
          this._createInfoWIndow(marker, markerPoint, markerCustomProp);
        }
        this.setState(() => ({ markers: markers }));
        this._map.setViewport(points); // set map viewport containing all points to show the markers
      }
    } catch (error) {
      console.error(error);
    }
  };

  _clearPracticeMarkers = () => {
    this._map.clearOverlays();
  };

  _showPracticeOnSelectMarker = (markerCustomProp) => {
    const { practices } = this.props;
    const practice = practices.find(
      (p) => p.practiceId === markerCustomProp.point.practiceId
    );

    Events.emit(PubSubEvents.practiceSelected, practice);

    this._mapSearchAreaButton.classList.remove(
      this.use_mobile_view
        ? 'mobile__map__search-area--visible'
        : 'map__search-area--visible'
    );
  };

  _createInfoWIndow = (marker, markerPoint, markerCustomProp) => {
    const { practices } = this.props;
    const practice = practices.find(
      (p) => p.practiceId === markerCustomProp.point.practiceId
    );
    const { t, config } = this.props;
    const { bookingForm, appointmentUrl } = practice;

    //Create an info window for each marker
    const infoWindowOpts = {
      width: 300,
      enableCloseOnclick: true,
      title: practice.name,
    };

    const createInfoWindowDetails = () => {
      const bookingText =
        bookingForm === BookingFormOptions.salesforceForm
          ? t('text_booking_request_appointment')
          : t('text_book_now');
      const practiceContainer = document.createElement('div');

      const cityRegion = config.hide_city_province
        ? ''
        : `, ${practice.city} ${practice.region}`;

      const practiceAddress = document.createElement('div');
      practiceAddress.textContent = `${practice.address1}${
        practice.address2 ? ` ${practice.address2}` : ''
      }${cityRegion}`;
      practiceAddress.style.fontSize = '0.7rem';

      const practiceLinks = document.createElement('div');

      const hasBookButton =
        config.show_book_now && (appointmentUrl || bookingForm);
      practiceLinks.innerHTML = `
      <a href="tel:${practice.phone}">${practice.phone}</a> 
      <span>${hasBookButton ? bookingText : ''}</span>`;

      practiceContainer.appendChild(practiceAddress);
      practiceContainer.appendChild(practiceLinks);

      return practiceContainer;
    };

    const infoWindow = new window.BMap.InfoWindow(
      createInfoWindowDetails(),
      infoWindowOpts
    );

    // Open info window when marker is clicked
    marker.addEventListener('click', () => {
      this._map.panTo(marker.getPosition());
      this._map.openInfoWindow(infoWindow, markerPoint);
    });
  };

  _onSelectMarker = (practice) => {
    // show custom map info window
  };

  render() {
    return (
      <div
        className={
          this.props.isDetailsPage ? 'baidu-map--details-page' : 'baidu-map'
        }
        id="baidu-map"
        ref={this._mapRef}
      ></div>
    );
  }
}

BaiduMap.propTypes = {
  practices: PropTypes.array,
  practice: PropTypes.object,
  t: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  searchLocationCoords: PropTypes.object,
  isDetailsPage: PropTypes.bool,
  isFirstLoad: PropTypes.bool,
};

BaiduMap.defaultProps = {
  practices: [],
};

export default withConfig(withRouter(withTranslation()(BaiduMap)));
